import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import alphalogo from '../../../utils/alphahorizontal.png';

// Función para crear estilos dinámicamente usando los colores seleccionados por el cliente
const createStyles = (primaryColor = '#03178c', secondaryColor = '#0066cc') => {
  const colors = {
    primary: primaryColor,
    secondary: secondaryColor,
    gray: {
      50: '#F2F2F2',
      100: '#F1F4F8',
      200: '#E3E8EF',
      300: '#D2DDEB',
      800: '#3B506C',
    },
  };

  return StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      padding: 20,
      paddingBottom: 40,
    },
    header: {
      backgroundColor: '#FFFFFF',
      padding: 12,
      borderRadius: 8,
      marginBottom: 20,
      border: `1px solid ${colors.secondary}`,
    },
    headerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 15,
    },
    headerLeft: {
      width: '30%',
    },
    headerLogoContainer: {
      backgroundColor: 'white',
      padding: 6,
      borderRadius: 8,
      border: `1px solid ${colors.gray[200]}`,
    },
    headerLogo: {
      width: '100%',
      height: 32,
      objectFit: 'contain',
      borderRadius: 6,
    },
    headerRight: {
      width: '35%',
      backgroundColor: colors.primary,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 6,
      justifyContent: 'center',
    },
    headerTitle: {
      fontSize: 9,
      color: 'white',
      fontWeight: 'bold',
      textAlign: 'center',
    },
    headerCenter: {
      width: '30%',
      justifyContent: 'center',
    },
    headerInfo: {
      flexDirection: 'column',
    },
    headerInfoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray[100],
      paddingVertical: 3,
      paddingHorizontal: 6,
      borderRadius: 4,
      marginBottom: 2,
    },
    headerValue: {
      fontSize: 9,
      color: colors.primary,
      flex: 1,
    },
    // Estilos para cada registro de mantenimiento
    itemContainer: {
      backgroundColor: 'white',
      padding: 6,
      borderRadius: 4,
      marginBottom: 4,
      border: `1px solid ${colors.gray[200]}`,
      flexDirection: 'row',
      alignItems: 'center',
    },
    colArea: { width: '12%' },
    colServicio: { width: '12%' },
    colUbicacion: { width: '12%' },
    colFecha: { width: '10%' },
    colMes: { width: '10%' },
    colActividades: { width: '34%' },
    colEstado: { width: '10%' },
    itemLabel: {
      fontSize: 5,
      color: colors.primary,
      fontWeight: 'bold',
      marginBottom: 2,
    },
    itemValue: {
      fontSize: 6,
      color: colors.gray[800],
    },
    footer: {
      position: 'absolute',
      bottom: 25,
      left: 25,
      right: 25,
      borderTop: `1px solid ${colors.gray[200]}`,
      paddingTop: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    footerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    footerLogo: {
      width: 100,
      height: 20,
    },
    footerWeb: {
      fontSize: 8,
      color: colors.primary,
      fontWeight: 'bold',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 6,
      bottom: 15,
      right: 25,
      color: colors.gray[800],
    },
  });
};

const PlanMtoDocuPDF = ({ equipos, cliente }) => {
  // Se usan los colores escogidos por el cliente
  const styles = createStyles(cliente?.primaryColor, cliente?.secondaryColor);
  // Se selecciona el logo según el tipo de plantilla del cliente
  const headerLogo = cliente?.plantillaType === 'prestador' ? cliente?.imagenurl : cliente?.logoUrl;
  
  // Se ordena el listado por el campo "mes"
  const sortedEquipos = equipos ? equipos.sort((a, b) => a.mes - b.mes) : [];

  // Header con información y logo del cliente
  const Header = () => (
    <View style={styles.header} fixed>
      <View style={styles.headerContent}>
        <View style={styles.headerLeft}>
          <View style={styles.headerLogoContainer}>
            <Image src={headerLogo || alphalogo} style={styles.headerLogo} />
          </View>
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.headerTitle}>
            PLAN DE MANTENIMIENTO DE INFRAESTRUCTURA
          </Text>
        </View>
        <View style={styles.headerCenter}>
          <View style={styles.headerInfo}>
            <View style={styles.headerInfoItem}>
              <Text style={styles.headerValue}>{cliente?.nombre || 'No disponible'}</Text>
            </View>
            <View style={styles.headerInfoItem}>
              <Text style={styles.headerValue}>{cliente?.ccnit || 'No disponible'}</Text>
            </View>
            <View style={styles.headerInfoItem}>
              <Text style={styles.headerValue}>{cliente?.ciudad || 'No disponible'}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  // Footer de la plantilla
  const Footer = () => (
    <View style={styles.footer} fixed>
      <View style={styles.footerLeft}>
        <Image src={alphalogo} style={styles.footerLogo} />
      </View>
      <View>
        <Text style={styles.footerWeb}>www.alphabiomedica.com</Text>
      </View>
    </View>
  );

  // Función para renderizar cada registro de mantenimiento
  const renderItem = (equipo) => (
    <View style={styles.itemContainer} key={equipo.progMtoId}>
      <View style={styles.colArea}>
        <Text style={styles.itemLabel}>Area</Text>
        <Text style={styles.itemValue}>{equipo.nombre}</Text>
      </View>
      <View style={styles.colServicio}>
        <Text style={styles.itemLabel}>Servicio</Text>
        <Text style={styles.itemValue}>{equipo.servicio}</Text>
      </View>
      <View style={styles.colUbicacion}>
        <Text style={styles.itemLabel}>Ubicación</Text>
        <Text style={styles.itemValue}>{equipo.ubicacion}</Text>
      </View>
      <View style={styles.colFecha}>
        <Text style={styles.itemLabel}>Fecha</Text>
        <Text style={styles.itemValue}>{equipo.fecha}</Text>
      </View>
      <View style={styles.colMes}>
        <Text style={styles.itemLabel}>Mes</Text>
        <Text style={styles.itemValue}>{equipo.mesnombre}</Text>
      </View>
      <View style={styles.colActividades}>
        <Text style={styles.itemLabel}>Actividades</Text>
        <Text style={styles.itemValue}>
          {equipo.taskElectrica}, {equipo.taskGases}, {equipo.taskComunicaciones}, {equipo.taskHidraulica}, {equipo.taskInstalaciones}
        </Text>
      </View>
      <View style={styles.colEstado}>
        <Text style={styles.itemLabel}>Estado</Text>
        <Text style={styles.itemValue}>{equipo.estado}</Text>
      </View>
    </View>
  );

  // Dividir en páginas si el listado es muy extenso
  const itemsPerPage = 14;
  const pages = [];
  for (let i = 0; i < sortedEquipos.length; i += itemsPerPage) {
    pages.push(sortedEquipos.slice(i, i + itemsPerPage));
  }

  return (
    <Document>
      {pages.map((pageItems, pageIndex) => (
        <Page size="A4" style={styles.page} key={pageIndex}>
          <Header />
          {pageItems.map(renderItem)}
          <Footer />
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
            fixed
          />
        </Page>
      ))}
    </Document>
  );
};

export default PlanMtoDocuPDF;
